import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Wallet as WalletIcon, AlertTriangle, ArrowDownRight, ArrowUpRight, Clock, RefreshCw, Activity, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const formatStable = (n, asset) => `${(n ?? 0).toLocaleString("en-IE", { maximumFractionDigits: 2 })} ${asset}`;
const formatEur = (n) => `€${(n ?? 0).toLocaleString("en-IE", { maximumFractionDigits: 2 })}`;

export default function AdminTreasury() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // B4: XGate Balance + Reconciliation
  const [xgateBal, setXgateBal] = useState(null);
  const [xgateBalLoading, setXgateBalLoading] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/treasury/float")
       .then((r) => setData(r.data))
       .finally(() => setLoading(false));
    loadXgateBalances();
  }, []);

  const loadXgateBalances = async () => {
    setXgateBalLoading(true);
    try {
      const r = await api.get("/admin/treasury/xgate-balances");
      setXgateBal(r.data);
    } catch (e) {
      toast.error("Failed to load XGate balances");
    } finally { setXgateBalLoading(false); }
  };

  const loadRecon = async () => {
    setReconLoading(true);
    try {
      const r = await api.get("/admin/treasury/xgate-reconciliation");
      setRecon(r.data);
    } catch (e) {
      toast.error("Failed to load reconciliation report");
    } finally { setReconLoading(false); }
  };

  if (loading) return <AdminLayout title="Treasury & float"><div className="text-sm text-muted-foreground">Loading…</div></AdminLayout>;

  const thresholds = data?.thresholds || {};
  const renderAsset = (asset) => {
    const a = data.by_asset[asset];
    const fronted = -a.crypto_out_total;
    const received = a.crypto_in_total;
    const open = fronted - received;
    const lowWarn = thresholds.low_warning_eur || 0;
    const alertState = fronted > 0 && (lowWarn - fronted) < 0 ? "ok" : "low";
    return (
      <div key={asset} className="xch-card p-5 space-y-3" data-testid={`treasury-${asset}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WalletIcon className="w-4 h-4 text-primary" />
            <div className="font-display text-lg font-bold text-foreground">{asset}</div>
          </div>
          {alertState === "low" && (
            <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded-full">
              <AlertTriangle className="w-3 h-3" /> Watch float
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={<ArrowDownRight className="w-3 h-3 text-rose-600" />}
                label="Fronted (out)" value={formatStable(fronted, asset)} testid={`stat-fronted-${asset}`} />
          <Stat icon={<ArrowUpRight className="w-3 h-3 text-emerald-600" />}
                label="Received (in)" value={formatStable(received, asset)} testid={`stat-received-${asset}`} />
          <Stat icon={<Clock className="w-3 h-3 text-amber-600" />}
                label="Pending T+1" value={formatStable(a.pending_receivable, asset)} testid={`stat-pending-${asset}`} />
          <Stat label="Net exposure" value={formatStable(open, asset)} testid={`stat-exposure-${asset}`}
                tone={open > thresholds.batch_settle_floor_eur ? "warn" : "neutral"} />
        </div>
      </div>
    );
  };

  return (
    <AdminLayout title="Treasury & float"
                 subtitle="Stablecoin float position, XGate exposure, T+1 receivables.">
      <div className="grid md:grid-cols-2 gap-4 mb-5" data-testid="treasury-grid">
        {renderAsset("USDC")}
        {renderAsset("USDT")}
      </div>

      {/* B4 — XGate Balance API */}
      <div className="xch-card p-5 mb-5" data-testid="treasury-xgate-balances">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-base font-bold text-foreground flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" /> XGate-side balance
            </div>
            <div className="text-xs text-muted-foreground">
              Live snapshot from XGate's Balance API. Compares against our books — flag means our records disagree.
            </div>
          </div>
          <Button onClick={loadXgateBalances} variant="outline" size="sm"
                  disabled={xgateBalLoading}
                  className="rounded-xl gap-1.5"
                  data-testid="xgate-balances-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${xgateBalLoading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {!xgateBal ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : !xgateBal.wired ? (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"
               data-testid="xgate-balances-not-wired">
            <AlertCircle className="w-3.5 h-3.5 inline mr-1.5" />
            {xgateBal.note || "XGate API not wired. Open Integrations to set credentials."}
          </div>
        ) : xgateBal.error ? (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900"
               data-testid="xgate-balances-error">
            <AlertCircle className="w-3.5 h-3.5 inline mr-1.5" />
            {xgateBal.error} — retry, or check XGate sandbox status.
          </div>
        ) : Object.keys(xgateBal.balances || {}).length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="xgate-balances-empty">
            XGate returned no balance rows. Either no funds in the sandbox account or the API endpoint shape changed.
          </div>
        ) : (
          <div className="space-y-2" data-testid="xgate-balances-rows">
            {Object.entries(xgateBal.balances).map(([asset, vals]) => {
              const cmp = xgateBal.comparison?.[asset];
              return (
                <div key={asset} className="grid grid-cols-2 md:grid-cols-5 gap-3 rounded-xl bg-muted/30 p-3"
                     data-testid={`xgate-balance-row-${asset}`}>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Asset</div>
                    <div className="font-display font-bold text-foreground">{asset}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Available</div>
                    <div className="font-mono text-sm font-bold tabular-nums">{formatStable(vals.available, asset)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Pending</div>
                    <div className="font-mono text-sm font-bold tabular-nums">{formatStable(vals.pending, asset)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Our pending</div>
                    <div className="font-mono text-sm font-bold tabular-nums">{formatStable(cmp?.internal_pending ?? 0, asset)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Δ</div>
                    <div className="flex items-center gap-1">
                      <div className={`font-mono text-sm font-bold tabular-nums ${cmp?.flag === "ok" ? "text-foreground" : cmp?.flag === "xgate_higher" ? "text-emerald-700" : "text-rose-700"}`}>
                        {formatStable(cmp?.delta ?? 0, asset)}
                      </div>
                      {cmp?.flag === "ok" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                      {cmp?.flag === "internal_higher" && <AlertCircle className="w-3.5 h-3.5 text-rose-600" data-testid={`xgate-delta-warn-${asset}`} />}
                    </div>
                  </div>
                </div>
              );
            })}
            {xgateBal.fetched_at && (
              <div className="text-[10px] text-muted-foreground mt-1">
                Fetched at {new Date(xgateBal.fetched_at).toLocaleString("en-IE")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* B4 — Reconciliation report */}
      <div className="xch-card p-5 mb-5" data-testid="treasury-xgate-recon">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-base font-bold text-foreground flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-primary" /> XGate reconciliation report
            </div>
            <div className="text-xs text-muted-foreground">
              Last 7 days of XGate settlement batches. Cross-check against our `settlement_batches` table — anything XGate reports but we don't have means a missed callback.
            </div>
          </div>
          <Button onClick={loadRecon} variant="outline" size="sm"
                  disabled={reconLoading}
                  className="rounded-xl gap-1.5"
                  data-testid="xgate-recon-load-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${reconLoading ? "animate-spin" : ""}`} /> {recon ? "Refresh" : "Load report"}
          </Button>
        </div>
        {!recon ? (
          <div className="text-xs text-muted-foreground">
            Hit "Load report" to fetch the last 7 days of XGate settlements.
          </div>
        ) : !recon.wired ? (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="xgate-recon-not-wired">
            <AlertCircle className="w-3.5 h-3.5 inline mr-1.5" />
            {recon.note || "XGate API not wired."}
          </div>
        ) : recon.error ? (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900" data-testid="xgate-recon-error">
            <AlertCircle className="w-3.5 h-3.5 inline mr-1.5" />
            {recon.error}
          </div>
        ) : (recon.rows || []).length === 0 ? (
          <div className="text-xs text-muted-foreground" data-testid="xgate-recon-empty">
            No settlement batches in the window {recon.from} → {recon.to}.
          </div>
        ) : (
          <div data-testid="xgate-recon-rows">
            <div className="text-[10px] text-muted-foreground mb-2">
              Window: {recon.from} → {recon.to} · {recon.rows.length} batch{recon.rows.length === 1 ? "" : "es"}
            </div>
            <div className="space-y-1.5">
              {recon.rows.map((row, i) => (
                <div key={row.id || i} className="grid grid-cols-2 md:grid-cols-5 gap-3 rounded-xl bg-muted/30 p-2.5 text-xs">
                  <div className="font-mono text-[10px] text-muted-foreground truncate">{row.id || row.batch_id || "—"}</div>
                  <div>{row.date || row.settled_at || row.timestamp || "—"}</div>
                  <div className="font-mono">{formatEur(row.amount_eur ?? row.amount ?? 0)}</div>
                  <div>{(row.asset || row.currency || "—").toUpperCase()}</div>
                  <div>
                    <Badge variant="outline" className="text-[9px] uppercase font-bold">{row.status || "settled"}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="xch-card p-5" data-testid="treasury-thresholds">
        <div className="font-display text-base font-bold text-foreground mb-3">Alert thresholds</div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <Threshold label="Low-warning" value={`€${(thresholds.low_warning_eur || 0).toLocaleString("en-IE")}`} desc="Push alert to staff to top up." />
          <Threshold label="Critical" value={`€${(thresholds.critical_eur || 0).toLocaleString("en-IE")}`} desc="Stop accepting new orders." />
          <Threshold label="XGate batch floor" value={`€${(thresholds.batch_settle_floor_eur || 0).toLocaleString("en-IE")}`} desc="Min volume before XGate settles." />
        </div>
      </div>
    </AdminLayout>
  );
}

const Stat = ({ icon, label, value, tone, testid }) => (
  <div className="rounded-xl bg-muted/30 p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1">
      {icon}{label}
    </div>
    <div className={`font-mono text-sm font-bold tabular-nums mt-0.5 ${tone === "warn" ? "text-rose-700" : "text-foreground"}`}>
      {value}
    </div>
  </div>
);

const Threshold = ({ label, value, desc }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
    <div className="font-display text-lg font-bold text-foreground">{value}</div>
    <div className="text-[11px] text-muted-foreground">{desc}</div>
  </div>
);
